import React, { useCallback, useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { AlertBanner } from '@flowcore-water/ui';
import { useAuth } from '../../contexts/AuthContext';
import { libraryService } from '../../services/libraryService';
import { useData } from '../../contexts/DataContext';
import { useTheme } from '@flowcore-water/ui';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeSlug from 'rehype-slug';
import { CommentSection } from './CommentSection';
import type { LibraryItem } from '../../types';
import { getManualSection, MANUAL_PAGE_PATH } from '../../data/trainingManual';
import flowcoreLogo from '../../assets/flowcore-logo.svg';

export const LibraryItemView: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { qualifications: qualificationsData } = useData();
  const { t } = useTheme();
  const [item, setItem] = useState<LibraryItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!slug) return;
    const load = async () => {
      try {
        setLoading(true);
        const data = await libraryService.getBySlug(slug);
        if (!data) setError('Item not found');
        else setItem(data);
      } catch (err) {
        console.error('Error loading library item:', err);
        setError('Failed to load item');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [slug]);

  const getLinkedQuals = (qualIds: string[]) => {
    return qualIds
      .map((id) => qualificationsData.find((q) => q.id === id))
      .filter(Boolean) as typeof qualificationsData;
  };

  const handlePrint = useCallback(() => {
    if (!item || item.type !== 'article') return;
    const contentEl = document.getElementById('article-print-content');
    if (!contentEl) return;
    const html = contentEl.innerHTML;

    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    printWindow.document.write(`<!DOCTYPE html>
<html><head><title>${item.title}</title>
<style>
  @page {
    size: letter;
    margin: 0.75in 1in 1in 1in;
    @bottom-center {
      content: "${item.title.replace(/"/g, '\\"')}  —  Page " counter(page) " of " counter(pages);
      font-size: 9pt;
      color: #64748b;
      font-family: system-ui, -apple-system, sans-serif;
    }
  }
  * { margin: 0; padding: 0; box-sizing: border-box; }
  body {
    font-family: system-ui, -apple-system, 'Segoe UI', sans-serif;
    font-size: 11pt;
    line-height: 1.6;
    color: #1e293b;
  }
  .letterhead {
    text-align: center;
    padding-bottom: 16pt;
    margin-bottom: 20pt;
    border-bottom: 2px solid #192530;
  }
  .letterhead img { height: 48pt; }
  .title-block {
    margin-bottom: 20pt;
  }
  .title-block h1 {
    font-size: 20pt;
    font-weight: 700;
    color: #0f172a;
    margin-bottom: 4pt;
  }
  .title-block .meta {
    font-size: 9pt;
    color: #64748b;
  }
  .content h1, .content h2 { font-size: 15pt; font-weight: 700; color: #0f172a; margin: 18pt 0 8pt; }
  .content h3 { font-size: 13pt; font-weight: 600; color: #1e293b; margin: 14pt 0 6pt; }
  .content h4 { font-size: 11pt; font-weight: 600; color: #1e293b; margin: 12pt 0 4pt; }
  .content p { margin-bottom: 10pt; }
  .content ul, .content ol { margin: 0 0 10pt 20pt; }
  .content li { margin-bottom: 3pt; }
  .content blockquote {
    border-left: 3px solid #192530;
    padding-left: 12pt;
    margin: 10pt 0;
    color: #475569;
    font-style: italic;
  }
  .content code {
    font-family: 'SF Mono', 'Fira Code', monospace;
    font-size: 9.5pt;
    background: #f1f5f9;
    padding: 1pt 4pt;
    border-radius: 3pt;
  }
  .content pre code {
    display: block;
    padding: 8pt 12pt;
    overflow-wrap: break-word;
    white-space: pre-wrap;
  }
  .content table {
    width: 100%;
    border-collapse: collapse;
    margin: 10pt 0;
    font-size: 10pt;
  }
  .content th, .content td {
    border: 1px solid #cbd5e1;
    padding: 4pt 8pt;
    text-align: left;
  }
  .content th { background: #f1f5f9; font-weight: 600; }
  .content a { color: #192530; text-decoration: underline; }
  .content img { max-width: 100%; }
  .footer-fallback {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    text-align: center;
    font-size: 9pt;
    color: #64748b;
    padding: 8pt 0;
  }
</style>
</head><body>
<div class="letterhead"><img src="${flowcoreLogo}" alt="Flowcore Water" /></div>
<div class="title-block">
  <h1>${item.title}</h1>
  <div class="meta">v${item.version} • ${new Date(item.updatedAt).toLocaleDateString()}</div>
</div>
<div class="content">${html}</div>
</body></html>`);
    printWindow.document.close();
    setTimeout(() => { printWindow.print(); }, 300);
  }, [item]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="" style={{ color: t.textMuted }}>Loading...</div>
      </div>
    );
  }

  if (error || !item) {
    return (
      <div className="space-y-4">
        <Link
          to="/library"
          className="inline-flex items-center gap-2  hover:text-cyan-300 transition-colors" style={{ color: t.accent }}
        >
          ← Back to Library
        </Link>
        <AlertBanner variant="error" message={error || 'Item not found'} />
      </div>
    );
  }

  const linkedQuals = getLinkedQuals(item.linkedQualIds);
  const canEdit = user?.role === 'instructor' || user?.role === 'admin';
  const canDelete = user?.role === 'admin';

  const manualInfo = getManualSection(item.manualSection);

  return (
    <div className="space-y-6">
      <Link
        to={manualInfo ? MANUAL_PAGE_PATH : '/library'}
        className="inline-flex items-center gap-2  hover:text-cyan-300 transition-colors" style={{ color: t.accent }}
      >
        ← Back to {manualInfo ? 'Water Well Technician Training' : 'Library'}
      </Link>

      <div className="rounded-lg border   p-8">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 mb-6">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <h1 className="text-4xl font-bold " style={{ color: t.textPrimary }}>{item.title}</h1>
              <span
                className="text-xs font-bold rounded-full px-2.5 py-1 border whitespace-nowrap"
                style={item.type === 'article'
                  ? { backgroundColor: `${t.accent}20`, color: t.accent, borderColor: `${t.accent}4D` }
                  : item.type === 'pdf'
                  ? { backgroundColor: '#f59e0b20', color: '#fbbf24', borderColor: '#f59e0b4D' }
                  : { backgroundColor: '#8b5cf620', color: '#a78bfa', borderColor: '#8b5cf64D' }}
              >
                {item.type === 'article' ? 'Article' : item.type === 'pdf' ? 'Attachment' : 'Web Link'}
              </span>
            </div>
            <p className="" style={{ color: t.textMuted }}>
              Uploaded by {item.createdByName || 'Unknown'} on{' '}
              {new Date(item.createdAt).toLocaleDateString()}
              {item.version > 1 && (
                <> • Updated by {item.updatedByName || 'Unknown'} on{' '}
                {new Date(item.updatedAt).toLocaleDateString()}</>
              )}
              {' '}• v{item.version}
            </p>
            {item.version > 1 && (
              <div className="mt-2 rounded-lg bg-amber-900/20 border border-amber-700 px-3 py-2">
                <p className="text-sm font-medium text-amber-400">
                  Change: {item.changeSummary}
                </p>
              </div>
            )}
            {item.description && (
              <p className="mt-3  leading-relaxed" style={{ color: t.textSecondary }}>{item.description}</p>
            )}
          </div>
          <div className="flex gap-2">
              {item.type === 'article' && (
                <button
                  onClick={handlePrint}
                  className="rounded-lg px-4 py-2 font-medium transition-colors whitespace-nowrap border"
                  style={{ borderColor: t.border, color: t.textSecondary, background: 'transparent' }}
                >
                  Print
                </button>
              )}
              {canEdit && (
                <button
                  onClick={() => navigate(`/library/edit/${item.id}`)}
                  className="rounded-lg bg-cyan-600 px-4 py-2 font-medium  hover:bg-cyan-500 transition-colors whitespace-nowrap" style={{ color: t.textPrimary }}
                >
                  Edit
                </button>
              )}
              {canDelete && (
                <button
                  onClick={async () => {
                    if (!user) return;
                    if (!confirm(`Delete "${item.title}"? It can be restored within 90 days.`)) return;
                    try {
                      console.log('[Delete] Starting soft-delete for:', item.id, item.title);
                      await libraryService.softDelete(item.id, user.uid);
                      console.log('[Delete] Soft-delete succeeded, dismissing attestations...');
                      try {
                        const { attestationService } = await import('../../services/attestationService');
                        await attestationService.dismissForArticle(item.id, user.uid);
                        console.log('[Delete] Attestations dismissed');
                      } catch (attErr) {
                        console.warn('[Delete] Could not dismiss attestations:', attErr);
                      }
                      console.log('[Delete] Navigating to /library with deletedId:', item.id);
                      navigate('/library', { state: { deletedId: item.id } });
                    } catch (err) {
                      console.error('[Delete] Failed:', err);
                      alert('Delete failed — check console for details.');
                    }
                  }}
                  className="rounded-lg bg-red-600/20 border border-red-500/30 px-4 py-2 font-medium  hover:bg-red-600/40 transition-colors whitespace-nowrap" style={{ color: t.fail }}
                >
                  Delete
                </button>
              )}
            </div>
        </div>

        {/* Content — type-specific */}
        {item.type === 'article' && (
          <div id="article-print-content" className="prose prose-invert max-w-none mb-8">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              rehypePlugins={[rehypeSlug]}
              components={{
                h1: (props: any) => (
                  <h2 className="text-2xl font-bold  mt-6 mb-3" style={{ color: t.textPrimary }} {...props} />
                ),
                h2: (props: any) => (
                  <h3 className="text-xl font-bold  mt-5 mb-2" style={{ color: t.textPrimary }} {...props} />
                ),
                h3: (props: any) => (
                  <h4 className="text-lg font-semibold  mt-4 mb-2" style={{ color: t.textSecondary }} {...props} />
                ),
                p: (props: any) => (
                  <p className=" mb-4 leading-relaxed" style={{ color: t.textSecondary }} {...props} />
                ),
                ul: (props: any) => (
                  <ul className="list-disc list-inside  mb-4 space-y-1" style={{ color: t.textSecondary }} {...props} />
                ),
                ol: (props: any) => (
                  <ol className="list-decimal list-inside  mb-4 space-y-1" style={{ color: t.textSecondary }} {...props} />
                ),
                li: (props: any) => <li className="" style={{ color: t.textSecondary }} {...props} />,
                blockquote: (props: any) => (
                  <blockquote
                    className="border-l-4 border-cyan-600  pl-4 py-2 my-4  italic" style={{ color: t.textSecondary }}
                    {...props}
                  />
                ),
                code: ({ inline, ...props }: any) =>
                  inline ? (
                    <code className=" text-cyan-300 px-2 py-1 rounded font-mono text-sm" {...props} />
                  ) : (
                    <code className=" text-cyan-300 px-3 py-2 rounded font-mono text-sm block my-4 overflow-auto" {...props} />
                  ),
                a: (props: any) => (
                  <a className=" hover:text-cyan-300 underline" style={{ color: t.accent }} {...props} />
                ),
              }}
            >
              {item.body}
            </ReactMarkdown>
          </div>
        )}

        {item.type === 'pdf' && (
          <div className="mb-8 space-y-4">
            {item.fileUrl ? (
              /\.(png|jpe?g|gif|webp|svg|bmp)(\?|$)/i.test(item.fileUrl) ? (
                <div className="rounded-lg border  overflow-hidden bg-white flex items-center justify-center">
                  <img src={item.fileUrl} alt={item.title} className="w-full h-auto object-contain" style={{ maxHeight: '70vh' }} />
                </div>
              ) : (
                <div className="rounded-lg border  overflow-hidden" style={{ height: '70vh' }}>
                  <iframe src={item.fileUrl} className="w-full h-full" title={item.title} style={{ background: '#fff' }} />
                </div>
              )
            ) : (
              <div className="rounded-lg border   px-6 py-12 text-center">
                <p className="" style={{ color: t.textMuted }}>No attachment yet</p>
              </div>
            )}
          </div>
        )}

        {item.type === 'weblink' && (
          <div className="mb-8 space-y-4">
            <a
              href={item.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-lg bg-cyan-600/20 border border-cyan-500/30 px-4 py-3  hover:bg-cyan-600/30 transition-colors" style={{ color: t.accent }}
            >
              <span className="text-lg">↗</span>
              <span className="font-medium">{item.url}</span>
            </a>
            {item.screenshotUrl && (
              <div className="rounded-lg border  overflow-hidden">
                <img
                  src={item.screenshotUrl}
                  alt={`Screenshot of ${item.title}`}
                  className="w-full"
                />
              </div>
            )}
          </div>
        )}

        {/* Linked Qualifications */}
        {linkedQuals.length > 0 && (
          <div className="border-t  pt-6 mb-6">
            <h3 className="text-lg font-semibold  mb-4" style={{ color: t.textPrimary }}>
              Linked Qualifications
            </h3>
            <div className="flex flex-wrap gap-2">
              {linkedQuals.map((qual) => (
                <Link
                  key={qual.id}
                  to="/tree"
                  className="rounded-full bg-cyan-600/20 border border-cyan-500/30 px-3 py-1.5 text-sm font-medium  hover:border-cyan-400 transition-colors" style={{ color: t.accent }}
                >
                  {qual.shortName}
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Comments (for PDF and weblink types) */}
        {(item.type === 'pdf' || item.type === 'weblink') && (
          <div className="border-t  pt-6">
            <CommentSection libraryItemId={item.id} />
          </div>
        )}
      </div>
    </div>
  );
};
